import type { FC, ReactNode } from "react";
import { createContext, useCallback, useContext, useState } from "react";
import type { ComponentSize } from "@/types/component-props";

interface TopNavContextValue {
  /**
   * Handler for mobile menu toggle
   */
  onMobileMenuToggle?: () => void;
  /**
   * Size for selectors
   */
  selectorSize?: ComponentSize;
  /**
   * Additional navigation content
   */
  navigationContent?: ReactNode;
  /**
   * Set the mobile menu toggle handler
   */
  setOnMobileMenuToggle: (handler?: () => void) => void;
  /**
   * Set the selector size
   */
  setSelectorSize: (size?: ComponentSize) => void;
  /**
   * Set the navigation content
   */
  setNavigationContent: (content?: ReactNode) => void;
}

const TopNavContext = createContext<TopNavContextValue | undefined>(undefined);

export const useTopNav = () => {
  const context = useContext(TopNavContext);
  if (!context) {
    throw new Error("useTopNav must be used within TopNavProvider");
  }
  return context;
};

export const useTopNavOptional = () => {
  return useContext(TopNavContext);
};

interface TopNavProviderProps {
  children: ReactNode;
}

export const TopNavProvider: FC<TopNavProviderProps> = ({ children }) => {
  const [onMobileMenuToggle, setOnMobileMenuToggleState] = useState<
    (() => void) | undefined
  >();
  const [selectorSize, setSelectorSizeState] = useState<
    ComponentSize | undefined
  >();
  const [navigationContent, setNavigationContentState] = useState<
    ReactNode | undefined
  >();

  const setOnMobileMenuToggle = useCallback((handler?: () => void) => {
    setOnMobileMenuToggleState(() => handler);
  }, []);

  const setSelectorSize = useCallback((size?: ComponentSize) => {
    setSelectorSizeState(size);
  }, []);

  const setNavigationContent = useCallback((content?: ReactNode) => {
    setNavigationContentState(content);
  }, []);

  return (
    <TopNavContext
      value={{
        navigationContent,
        onMobileMenuToggle,
        selectorSize,
        setNavigationContent,
        setOnMobileMenuToggle,
        setSelectorSize,
      }}
    >
      {children}
    </TopNavContext>
  );
};
